var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createClient, createConfig } from "@hey-api/client-fetch";
import { getGoogleReCaptchaToken } from "@jti/fe-clients";
import { sanitizeBaseUrl } from "../../../utils";
import { articleRead, buyPrize, getConsumerLoyaltyProfile, getConsumerOrder, getConsumerPointsHistory, getConsumerRewardsCampaignsOrPrizes, getConsumerTier, getContestStatus, getEarningRuleByName, getEarningsForConsumer, getLoyaltyPublicDomainConfiguration, getQrCodeDetails, getReward, getTiers, indexEarnings, indexRewards, qrCodeRead, submitContest, } from "../../evo-client/__generated__";
import { contestsFormDataBodySerializer } from "./utils/contestsFormDataBodySerializer";
import { convertEvoGetPrizes, convertEvoPointsHistory, convertEvoPrizeTypeToRewardType, convertReferralHistoryEvoResponse, getArticlesFromEvoPayload, } from "./utils";
export class LoyaltyApiEvo {
    constructor(loyaltyApiUrl, loyaltyTenantToken, recaptchaId) {
        this.loyaltyTenantToken = loyaltyTenantToken;
        this.recaptchaId = recaptchaId;
        this.client = createClient(createConfig({
            baseUrl: sanitizeBaseUrl(loyaltyApiUrl),
            throwOnError: true,
        }));
    }
    getArticles(_a) {
        return __awaiter(this, arguments, void 0, function* ({ uuid, ciam_token = "" }) {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            if (!uuid) {
                const { data } = yield indexEarnings({
                    client: this.client,
                    headers: {
                        "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                        "X-DES-TENANT": this.loyaltyTenantToken,
                    },
                    query: {
                        type: "article",
                    },
                });
                return getArticlesFromEvoPayload(data === null || data === void 0 ? void 0 : data.data);
            }
            const { data } = yield getEarningsForConsumer({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                },
                path: {
                    uuid,
                },
            });
            return getArticlesFromEvoPayload(data === null || data === void 0 ? void 0 : data.data);
        });
    }
    getStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data } = yield getLoyaltyPublicDomainConfiguration({
                    client: this.client,
                    headers: {
                        "X-DES-TENANT": this.loyaltyTenantToken,
                    },
                });
                return Boolean(data === null || data === void 0 ? void 0 : data.is_active);
            }
            catch (e) {
                return false;
            }
        });
    }
    getPoints(uuid, ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            const failureGetPointsResponse = {
                points: 0,
                expire_at: "",
                pending_points: 0,
                points_to_expire: 0,
            };
            try {
                const { data } = yield getConsumerLoyaltyProfile({
                    client: this.client,
                    headers: {
                        "X-DES-USER-TOKEN": ciam_token,
                        "X-DES-TENANT": this.loyaltyTenantToken,
                    },
                    path: {
                        uuid: uuid,
                    },
                });
                if (!(data === null || data === void 0 ? void 0 : data.data)) {
                    return failureGetPointsResponse;
                }
                return data.data;
            }
            catch (e) {
                return failureGetPointsResponse;
            }
        });
    }
    getLoyaltyProfile(uuid, ciam_token, skipCache) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield getConsumerLoyaltyProfile({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-SKIP-SW-CACHE": skipCache,
                },
                path: {
                    uuid: uuid,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data))
                throw new Error("No data found in getLoyaltyProfile");
            return data;
        });
    }
    getEligibleCampaigns(uuid, ciam_token
    // order?: CampaignOrderData
    ) {
        return __awaiter(this, void 0, void 0, function* () {
            // not fully implemented, could not pass the order data
            try {
                const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
                const { data } = yield getConsumerRewardsCampaignsOrPrizes({
                    client: this.client,
                    headers: {
                        "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                        "X-DES-TENANT": this.loyaltyTenantToken,
                        "X-DES-USER-TOKEN": ciam_token,
                    },
                    path: {
                        uuid,
                    },
                    query: {
                        type: "campaign",
                    },
                });
                if (!data) {
                    return [];
                }
                return data.data.map((item) => {
                    return {
                        campaign_id: item.identifier,
                        points: item.points,
                        price: item.price,
                        is_eligible: item.has_coupons,
                    };
                });
            }
            catch (e) {
                return [];
            }
        });
    }
    getPointsHistory(consumer_uuid_1, ciamToken_1) {
        return __awaiter(this, arguments, void 0, function* (consumer_uuid, ciamToken, perPage = 10, page = 1) {
            const emptyGetPointsHistoryResponse = {
                items: [],
                statusCode: 400,
                total: 0,
            };
            try {
                const { data, response } = yield getConsumerPointsHistory({
                    client: this.client,
                    headers: {
                        "X-DES-TENANT": this.loyaltyTenantToken,
                        "X-DES-USER-TOKEN": ciamToken,
                    },
                    path: { uuid: consumer_uuid },
                    query: {
                        page,
                        per_page: perPage,
                        type: "history",
                    },
                });
                if (!(data === null || data === void 0 ? void 0 : data.data)) {
                    return Object.assign(Object.assign({}, emptyGetPointsHistoryResponse), { statusCode: response.status });
                }
                return convertEvoPointsHistory(data, response.status);
            }
            catch (e) {
                return emptyGetPointsHistoryResponse;
            }
        });
    }
    getTiers() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data, response } = yield getTiers({
                client: this.client,
                headers: {
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                },
            });
            return { data: (_a = data === null || data === void 0 ? void 0 : data.data) !== null && _a !== void 0 ? _a : [], statusCode: response.status };
        });
    }
    getUserTier(consumer_uuid, ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data } = yield getConsumerTier({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                path: {
                    uuid: consumer_uuid,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return {
                image_url: (_b = (_a = data.data) === null || _a === void 0 ? void 0 : _a.image_url) !== null && _b !== void 0 ? _b : "",
                name: data.data.name,
            };
        });
    }
    getUserTierData(consumer_uuid, ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, response } = yield getConsumerLoyaltyProfile({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                },
                path: {
                    uuid: consumer_uuid,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return Object.assign(Object.assign({}, data.data), { statusCode: response.status });
        });
    }
    getEarnedPoints(order_id_1, ciam_token_1) {
        return __awaiter(this, arguments, void 0, function* (order_id, ciam_token, uuid = "") {
            var _a, _b;
            const { data } = yield getConsumerOrder({
                client: this.client,
                headers: {
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-USER-TOKEN": ciam_token,
                },
                path: {
                    order_id,
                    uuid,
                },
            });
            return (_b = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.points) !== null && _b !== void 0 ? _b : 0;
        });
    }
    buyPrize(consumer_uuid, prize_uuid, qty, ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data, response } = yield buyPrize({
                client: this.client,
                headers: {
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                path: {
                    uuid: consumer_uuid,
                },
                body: {
                    reward_uuid: prize_uuid,
                    // SCHEMA misses the qty, // but the API expects it, so we need to pass it
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    qty,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return Object.assign(Object.assign({}, data.data), { statusCode: response.status, message: "" });
        });
    }
    getEarningRulePoints(event_name, _, user_uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
                const { data } = yield getEarningRuleByName({
                    client: this.client,
                    headers: {
                        "X-DES-TENANT": this.loyaltyTenantToken,
                        "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    },
                    path: {
                        event_name,
                        uuid: user_uuid,
                    },
                });
                if (!(data === null || data === void 0 ? void 0 : data.data)) {
                    return 0;
                }
                return data.data.points;
            }
            catch (e) {
                return 0;
            }
        });
    }
    getQrCodeDetails(_a) {
        return __awaiter(this, arguments, void 0, function* ({ uuid = "", qrCodeId, ciam_token = "", }) {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data } = yield getQrCodeDetails({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                },
                path: {
                    id: qrCodeId,
                    uuid,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return data.data;
        });
    }
    readArticleAction(article_id_1, ciam_token_1) {
        return __awaiter(this, arguments, void 0, function* (article_id, ciam_token, uuid = "") {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { response } = yield articleRead({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                body: {
                    article_id,
                },
                path: {
                    uuid,
                },
            });
            return { statusCode: response.status };
        });
    }
    scanQrAction(qr_code_id_1, ciam_token_1) {
        return __awaiter(this, arguments, void 0, function* (qr_code_id, ciam_token, uuid = "") {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data, response } = yield qrCodeRead({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                path: {
                    uuid,
                },
                body: {
                    qr_code_id,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return Object.assign(Object.assign({}, data.data), { statusCode: response.status });
        });
    }
    getContestStatus(uuid, ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data } = yield getContestStatus({
                client: this.client,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                path: {
                    uuid,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No Contest data found");
            }
            return data.data;
        });
    }
    postContest(uuid, ciam_token, contestSubmitData) {
        return __awaiter(this, void 0, void 0, function* () {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            yield submitContest({
                client: this.client,
                body: contestSubmitData,
                headers: {
                    "X-DES-USER-TOKEN": ciam_token,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    "Content-Type": null,
                },
                path: {
                    uuid,
                },
                bodySerializer: contestsFormDataBodySerializer,
            });
        });
    }
    getPrizes(_a) {
        return __awaiter(this, arguments, void 0, function* ({ orderBy, page, perPage, sort, is_virtual, location_ids, }) {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data, response } = yield indexRewards({
                client: this.client,
                headers: {
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                    "X-DES-TENANT": this.loyaltyTenantToken,
                },
                query: {
                    page,
                    order_by: orderBy,
                    is_virtual: is_virtual,
                    location_ids,
                    per_page: perPage,
                    sort,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found");
            }
            return Object.assign(Object.assign({}, convertEvoGetPrizes(data)), { statusCode: response.status });
        });
    }
    getProductPoints() {
        return __awaiter(this, arguments, void 0, function* (sku = "", _quantity = 1, uuid = null, ciam_token = null) {
            var _a, _b, _c;
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const query = { identifier: [sku], type: "product" };
            const headers = {
                "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                "X-DES-TENANT": this.loyaltyTenantToken,
            };
            const { data } = uuid && ciam_token
                ? yield getEarningsForConsumer({
                    client: this.client,
                    headers: Object.assign(Object.assign({}, headers), { "X-DES-USER-TOKEN": ciam_token }),
                    path: { uuid },
                    query,
                })
                : yield indexEarnings({ client: this.client, headers, query });
            if (!(data === null || data === void 0 ? void 0 : data.data))
                throw new Error("No data found in getProductPoints");
            const product = data.data.find((item) => item.identifier === sku);
            if (!product) {
                return {
                    points: 0,
                    rules: [],
                    time_limited: false,
                };
            }
            return {
                points: product.points_without_multiply,
                rules: [
                    {
                        multiply: false, // EVO does not support multiply points for products as it does not support QTY
                        points: product.points_without_multiply,
                    },
                ],
                time_limited: (_c = (_b = (_a = product.validity_rules) === null || _a === void 0 ? void 0 : _a.time) === null || _b === void 0 ? void 0 : _b.limited) !== null && _c !== void 0 ? _c : false,
            };
        });
    }
    getReferralHistory(consumer_uuid_1, ciam_token_1) {
        return __awaiter(this, arguments, void 0, function* (consumer_uuid, ciam_token, perPage = 5, page = 1) {
            const { data } = yield getConsumerPointsHistory({
                client: this.client,
                headers: {
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-USER-TOKEN": ciam_token,
                },
                path: { uuid: consumer_uuid },
                query: {
                    type: "referral",
                    page,
                    per_page: perPage,
                },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found in getReferralHistory");
            }
            return convertReferralHistoryEvoResponse(data);
        });
    }
    getReward(prize_uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const recaptchaToken = yield getGoogleReCaptchaToken(this.recaptchaId);
            const { data } = yield getReward({
                client: this.client,
                headers: {
                    "X-DES-TENANT": this.loyaltyTenantToken,
                    "X-DES-RECAPTCHA-TOKEN": recaptchaToken,
                },
                path: { uuid: prize_uuid },
            });
            if (!(data === null || data === void 0 ? void 0 : data.data)) {
                throw new Error("No data found in getReward");
            }
            return Object.assign(Object.assign({}, data), { rewardType: convertEvoPrizeTypeToRewardType(data.data) });
        });
    }
    // NOT IMPLEMENTED METHODS IN EVO
    getAchievements(_userUuid, _ciam_token) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.reject("Not implemented");
        });
    }
}
//# sourceMappingURL=LoyaltyApiEvo.js.map